(ns practitest.frontend.ptql-editor
  (:require
    ["/codemirror/ptql-mode" :refer [ptqlLanguage]]
    ["@catppuccin/codemirror" :refer [catppuccinMocha]]
    ["@codemirror/autocomplete" :refer [autocompletion moveCompletionSelection
                                        startCompletion]]
    ["@codemirror/commands" :refer [defaultKeymap]]
    ["@codemirror/state" :refer [Compartment EditorState Prec]]
    ["@codemirror/view" :refer [EditorView keymap]]
    [clojure.string :as str]
    [practitest.frontend.ptql.parser :as ptql-parser]
    [reagent.core :as r]))

(defn- extract-last-field
  "Extract last field name (quoted or not) matching the given pattern."
  [text pattern]
  (when-let [m (re-find pattern text)]
    (or (nth m 1 nil) (nth m 2 nil))))

(defn- find-field-before-cursor [text]
  (extract-last-field text #"(?:'([^']+)'|(\w+))\s+(?:IS|=|!=|>=?|<=?)\s*$"))

(defn- linked-entity-in-scope
  "If cursor is inside a `LINKED TO <Entity> WHERE (...)` clause, return the entity name."
  [text]
  (loop [i (dec (count text)) depth 0]
    (cond
      (neg? i) nil
      (= (.charAt text i) \)) (recur (dec i) (inc depth))
      (= (.charAt text i) \() (if (zero? depth)
                                (when-let [m (re-find #"LINKED\s+TO\s+(\w+)\s+WHERE\s*$"
                                                      (subs text 0 i))]
                                  (nth m 1))
                                (recur (dec i) (dec depth)))
      :else (recur (dec i) depth))))

(defn- find-field-before-operator [text]
  (extract-last-field (str/trimr text) #"(?:'([^']+)'|(\w+))\s*$"))

(defn- keyword->completion [[op boost]]
  {:label op :type "keyword" :boost boost})

(defn- field->completion [field]
  {:label (if (str/includes? (:name field) " ")
            (str "'" (:name field) "'")
            (:name field))
   :type "variable"
   :detail (get field :type "")})

(defn- value->completion [value]
  {:label (str "\"" value "\"") :type "value"})

(defn- operators-per-field-type [field-type]
  (case field-type
    ("user" "list") (mapv keyword->completion [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2]
                                               ["=" 1] ["!=" 1]])
    "text" (mapv keyword->completion [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["STARTS WITH" 2] ["CONTAINS" 2]
                                      ["=" 1] ["!=" 1]])
    ("number" "date-time") (mapv keyword->completion [["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["IN LAST" 2] ["NOT IN LAST" 2]
                                                      ["=" 1] ["!=" 1] [">" 1] [">=" 1] ["<" 1] ["<=" 1]])
    ;; fallback
    (mapv keyword->completion
          [["IS" 2] ["IS EMPTY" 2] ["IS NOT EMPTY" 2] ["STARTS WITH" 2] ["CONTAINS" 2] ["IN LAST" 2] ["NOT" 2]
           ["=" 1] ["!=" 1] [">" 1] [">=" 1] ["<" 1] ["<=" 1]])))

(defn- field-by-name [fields name]
  (first (filter #(= (str/lower-case (:name %)) (str/lower-case name)) fields)))

(defn- expects-field-name? [expected]
  (or (contains? expected "field-name")
      (some #(str/includes? % "[a-zA-Z_]") expected)))

(defn- expects-value? [expected]
  (or (contains? expected "value")
      ;; NOTE: matches double-quoted string regex from grammar
      (some #(str/includes? % "[^\\\"]*") expected)))

(defn- field-name-completions [expected fields]
  (when (expects-field-name? expected)
    (map field->completion fields)))

(defn- field-value-completions [expected text-to-parse fields users groups]
  (when (expects-value? expected)
    (if (str/ends-with? (str/trimr text-to-parse) "GROUP")
      (map (comp value->completion :name) groups)
      (when-let [field (some->> (find-field-before-cursor text-to-parse)
                                (field-by-name fields))]
        (cond
          (= (:type field) "user")
          (concat
            (map (comp value->completion :email) users)
            [{:label "CURRENT_USER" :type "value" :boost 2}
             {:label "GROUP" :type "keyword" :boost 2}])

          (seq (:values field))
          (map value->completion (:values field)))))))

(defn- operator-completions [expected text-to-parse fields]
  (when (some #(contains? expected %) ["IS" "=" "!=" ">" ">=" "<" "<=" "STARTS" "CONTAINS" "IN" "NOT"])
    (when-let [field (some->> (find-field-before-operator text-to-parse)
                              (field-by-name fields))]
      (operators-per-field-type (:type field)))))

(defn- keyword-completions [expected]
  (concat
    (keep (fn [[k label]]
            (when (contains? expected k)
              (keyword->completion [label 2])))
          [["LINKED" "LINKED TO"]])
    (keep (fn [k]
            (when (contains? expected k)
              (keyword->completion [k 2])))
          ["WHERE" "AND" "OR" "Requirement" "Test" "TestSet" "Issue"
           "day" "days" "month" "months" "COUNT"])))

(defn- ptql-completion-source [fields-by-entity source-entity users groups]
  (fn [^js context]
    (let [word (.matchBefore context #"[\w\"']+")
          from (if word (.-from word) (.-pos context))
          text-to-parse (.sliceString (.-doc (.-state context)) 0 from)
          scope-entity (or (linked-entity-in-scope text-to-parse) source-entity)
          fields (get fields-by-entity scope-entity [])
          result (ptql-parser/parse text-to-parse)]
      (when (or word (.-explicit context))
        (clj->js
          {:from from
           :options (if (ptql-parser/failure? result)
                      (let [expected (ptql-parser/get-expected result)]
                        (concat
                          (field-name-completions expected fields)
                          (field-value-completions expected text-to-parse fields users groups)
                          (operator-completions expected text-to-parse fields)
                          (keyword-completions expected)))
                      (concat
                        (when (re-find #"LINKED\s+TO\s+\w+\s*$" text-to-parse)
                          [(keyword->completion ["WHERE" 3])])
                        (map keyword->completion [["AND" 2] ["OR" 2]])))})))))

(defn- parse-error-display [parse-error]
  (when-let [error @parse-error]
    [:div {:style {:color "#f38ba8"
                   :font-size "12px"
                   :font-family "monospace"
                   :padding "4px 12px"}}
     error]))

(defn- validate-ptql! [parse-error value]
  (reset! parse-error
          (when-not (str/blank? value)
            (let [result (ptql-parser/parse value)]
              (when (ptql-parser/failure? result)
                (let [idx (ptql-parser/get-failure-index result)]
                  (str "Syntax error at position " idx)))))))

(defn- make-extensions [{:keys [fields-by-entity source-entity users groups read-only? on-change on-submit parse-error]}]
  (let [read-only-c (Compartment.)]
    {:read-only-c read-only-c
     :extensions
     (cond-> [(.of keymap (clj->js defaultKeymap))
              (.of keymap (clj->js [{:key "Enter"
                                     :run (fn [^js view]
                                            (when on-submit
                                              (on-submit (.toString (.-doc (.-state view)))))
                                            true)}]))
              (.high Prec
                     (.of keymap (clj->js [{:key "Ctrl-n" :run (moveCompletionSelection true)}
                                           {:key "Ctrl-p" :run (moveCompletionSelection false)}])))
              ptqlLanguage
              catppuccinMocha
              (.-lineWrapping EditorView)
              (autocompletion (clj->js {:override [(ptql-completion-source
                                                     (or fields-by-entity {})
                                                     source-entity
                                                     users
                                                     groups)]
                                        :activateOnTyping true
                                        :aboveCursor true}))
              (.of read-only-c (.of (.-readOnly EditorState) (boolean read-only?)))
              (.of (.-inputHandler EditorView)
                   (fn [^js view _from _to text]
                     (when (= text " ")
                       (js/setTimeout #(startCompletion view) 0))
                     false))
              (.of (.-updateListener EditorView)
                   (fn [^js update]
                     (when (.-docChanged update)
                       (let [doc (.toString (.-doc (.-state update)))]
                         (when on-change (on-change doc))
                         (validate-ptql! parse-error doc)))))]
       read-only?
       (conj (.of (.-contentAttributes EditorView)
                  (clj->js {:tabindex "-1"}))))}))

(defn editor
  "CM6 PTQL editor component.

  Props:
  - `:value`: PTQL string
  - `:on-change`: `(fn [new-ptql-string])`
  - `:on-submit`: `(fn [ptql-string])` called on Enter/Ctrl+Enter
  - `:fields-by-entity`: map of PTQL entity name to field list, e.g.
    `{\"Issue\" [{:name \"Status\" :type \"list\" :values [\"Open\" ...]} ...]
      \"Test\" [...]
      ...}`
    Used for context-aware autocomplete: when the cursor is inside a `LINKED TO <Entity> WHERE (...)` clause,
    fields are sourced from <Entity>, otherwise from `:source-entity`.
  - `:source-entity`: PTQL entity name (e.g. \"Test\") - the default scope for autocomplete
  - `:users`: vector of user objects with key `:email`
  - `:groups`: vector of group objects with key `:name`
  - `:read-only?`: boolean
  - `:auto-focus?`: boolean - focus the editor on mount"
  [{:keys [value] :as opts}]
  (let [container-ref (atom nil)
        view-ref (atom nil)
        read-only-c-ref (atom nil)
        parse-error (r/atom nil)]
    (r/create-class
      {:display-name "ptql-editor"

       :component-did-mount
       (fn [_this]
         (let [dom-node @container-ref
               doc (or value "")
               {:keys [read-only-c extensions]} (make-extensions (assoc opts :parse-error parse-error))]
           (reset! read-only-c-ref read-only-c)
           (reset! view-ref
                   (EditorView. (clj->js {:state (.create EditorState (clj->js {:doc (or value "")
                                                                                :extensions extensions
                                                                                :selection {:anchor (count doc)}}))
                                          :parent dom-node})))
           (validate-ptql! parse-error value)
           (when (:auto-focus? opts)
             (.focus ^js @view-ref))))

       :component-did-update
       (fn [this [_ prev-props]]
         (when-let [v ^js @view-ref]
           (let [{:keys [value read-only?]} (r/props this)]
             ;; sync value if changed externally
             (when (and value (not= value (.toString (.-doc (.-state v)))))
               (.dispatch v
                          (clj->js {:changes {:from 0
                                              :to (.. v -state -doc -length)
                                              :insert value}})))
             ;; sync read-only
             (when (not= read-only? (:read-only? prev-props))
               (.dispatch v
                          (clj->js {:effects (.reconfigure @read-only-c-ref
                                                           (.of (.-readOnly EditorState) (boolean read-only?)))}))))))

       :component-will-unmount
       (fn [_]
         (when-let [v @view-ref]
           (.destroy v)
           (reset! view-ref nil)))

       :reagent-render
       (fn [_]
         [:div
          [:div.ptql-editor-container {:ref #(reset! container-ref %)}]
          [parse-error-display parse-error]])})))
